import { useState } from "react";
import axios from "axios";
import { ArrowRight, Download, BookOpen, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EBOOK_FILE = `${BACKEND_URL}/ebook/buy-like-a-pro.pdf`;

const highlights = [
  "The 7 principles of a pro buyer",
  "The psychology of the sale — and the seller",
  "The 10-point pre-offer checklist"
];

export default function EbookDownload() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [downloadUrl, setDownloadUrl] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setStatus({ state: "error", msg: "Please enter your name, email and phone." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const res = await axios.post(`${API}/ebook`, form);
      setDownloadUrl(res.data?.download_url || EBOOK_FILE);
      setStatus({ state: "success", msg: res.data?.message || "Your download is ready below." });
      setForm({ name: "", email: "", phone: "" });
    } catch (err) {
      setStatus({
        state: "error",
        msg: err.response?.data?.detail || "Something went wrong. Please try again."
      });
    }
  };

  return (
    <section id="ebook" data-testid="ebook-section" className="border-t border-neutral-800 bg-[#0A100D]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Pitch + cover */}
          <div>
            <p className="overline mb-4 text-[#36C281]">Free Guide</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight mb-6">
              Buy Like <span className="italic text-[#36C281]">a Pro</span>.
            </h2>
            <p className="text-neutral-400 leading-relaxed text-lg max-w-md mb-8">
              The buyer&apos;s field guide — the disciplined, execution-focused approach I use for my
              clients, distilled into a short read you can apply to your next purchase.
            </p>

            <ul className="space-y-3 mb-10" data-testid="ebook-highlights">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-neutral-300">
                  <Check size={18} className="text-[#36C281] mt-0.5 flex-shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>

            <div className="relative inline-block">
              <img
                src="/ebook/cover.png"
                alt="Buy Like a Pro — eBook cover"
                data-testid="ebook-cover-image"
                className="w-44 md:w-52 rounded-md shadow-2xl shadow-black/50 ring-1 ring-neutral-800"
              />
              <div className="absolute -top-3 -right-3 bg-[#36C281] text-[#0A100D] text-xs font-bold tracking-wide px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <BookOpen size={13} /> PDF
              </div>
            </div>
          </div>

          {/* Form / success */}
          <div className="bg-[#151E1A] border border-neutral-800 rounded-xl p-6 md:p-9 shadow-xl shadow-black/20">
            {status.state === "success" ? (
              <div data-testid="ebook-success" className="text-center py-6">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#36C281]/15 flex items-center justify-center mb-6">
                  <Check size={28} className="text-[#36C281]" />
                </div>
                <h3 className="font-serif-display text-3xl mb-3">You&apos;re all set.</h3>
                <p data-testid="ebook-status-msg" className="text-neutral-400 mb-8 max-w-sm mx-auto">
                  {status.msg}
                </p>
                <a
                  href={downloadUrl || EBOOK_FILE}
                  target="_blank"
                  rel="noreferrer"
                  download
                  data-testid="ebook-download-btn"
                  className="btn-primary inline-flex"
                >
                  Download the eBook <Download size={16} />
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="ebook-form" className="space-y-2">
                <p className="text-xs tracking-[0.2em] uppercase text-[#36C281]/80 mb-6">
                  Get your free copy
                </p>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Name</label>
                  <input
                    data-testid="ebook-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    className="line-input"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Email</label>
                  <input
                    data-testid="ebook-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@example.com"
                    className="line-input"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-neutral-500">Phone</label>
                  <input
                    data-testid="ebook-input-phone"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="04XX XXX XXX"
                    className="line-input"
                    autoComplete="tel"
                  />
                </div>

                <div className="pt-6 flex flex-col sm:flex-row sm:items-center gap-5">
                  <button
                    type="submit"
                    data-testid="ebook-submit-btn"
                    disabled={status.state === "loading"}
                    className="btn-primary"
                  >
                    {status.state === "loading" ? "Sending..." : "Send Me the eBook"} <ArrowRight size={16} />
                  </button>
                  {status.msg && status.state === "error" && (
                    <p data-testid="ebook-status-msg" className="text-sm text-red-400">
                      {status.msg}
                    </p>
                  )}
                </div>
                <p className="text-xs text-neutral-600 pt-2">
                  No spam. Just the guide and the occasional note worth reading.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
